/* Hook & Curve cart. Keeps the Dawn product-form/quick-add drawer contract. */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.root = window.Shopify?.routes?.root || '/';
    this.version = 0;
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    this.onClick = this.handleClick.bind(this);
    this.addEventListener('click', this.onClick);
    this.guardCheckout = (event) => {
      if (this.busy && event.target.closest('.hnc-cart-checkout')) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    };
    this.addEventListener('click', this.guardCheckout, true);
    this.onKeydown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.panel ? this.closePanel() : this.close();
      }
      if (event.key === 'Enter' && event.target.id === 'HncDiscountCode') {
        event.preventDefault();
        this.applyCoupon(event.target.value.trim());
      }
    };
    this.addEventListener('keydown', this.onKeydown);
    // Delegate so a replaced header section still opens the drawer.
    this.onHeaderClick = (event) => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href, window.location.href);
      const cartPath = new URL(this.root + 'cart', window.location.origin).pathname;
      if (url.origin !== window.location.origin || url.pathname.replace(/\/$/, '') !== cartPath.replace(/\/$/, '') || url.search) return;
      event.preventDefault();
      this.open(link);
      this.refresh().catch(() => this.status('Unable to refresh your cart. Please reopen it to try again.'));
    };
    document.addEventListener('click', this.onHeaderClick);
    const pageUrl = new URL(window.location.href);
    if (pageUrl.searchParams.get('hnc-cart') === 'open') {
      pageUrl.searchParams.delete('hnc-cart');
      window.history.replaceState(window.history.state, '', pageUrl.pathname + pageUrl.search + pageUrl.hash);
      this.open(document.querySelector('#cart-icon-bubble'));
    }
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'hnc-cart' || event.source === 'product-form') return;
      return this.refresh().catch(() => this.status('Unable to refresh your cart. Please reopen it to try again.'));
    });
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    document.removeEventListener('click', this.onHeaderClick);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('click', this.guardCheckout, true);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.classList.contains('active')) this.close();
    this.ready = false;
  }

  setActiveElement(element) { this.activeElement = element; }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (!this.activeElement) this.setActiveElement(document.activeElement);
    this.inert = false;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.focusDrawer();
  }

  close() {
    this.closePanel(false);
    this.classList.remove('active');
    removeTrapFocus(this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble'));
    this.inert = true;
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  focusDrawer(target) {
    if (!this.classList.contains('active')) return;
    const container = this.panel ? this.querySelector(`[data-hnc-sheet="${this.panel}"]`) : this.querySelector('.drawer__inner');
    if (container) trapFocus(container, target || container);
  }

  openPanel(name) {
    const sheet = this.querySelector(`[data-hnc-sheet="${name}"]`);
    if (!sheet) return;
    this.panel = name;
    sheet.hidden = false;
    sheet.classList.add('active');
    this.querySelector('.hnc-sheet-overlay').hidden = false;
    this.querySelector(`[data-hnc-panel="${name}"]`)?.setAttribute('aria-expanded', 'true');
    this.setBackgroundInert(true);
    this.focusDrawer(sheet.querySelector('input, textarea') || sheet);
  }

  setBackgroundInert(value) {
    this.querySelectorAll('.bluen-cart-header, .bluen-cart-scroll, .bluen-cart-footer').forEach(el => { el.inert = value; });
  }

  closePanel(restoreFocus = true) {
    const name = this.panel;
    this.querySelectorAll('[data-hnc-sheet]').forEach(sheet => { sheet.hidden = true; sheet.classList.remove('active'); });
    this.querySelector('.hnc-sheet-overlay')?.setAttribute('hidden', '');
    this.querySelectorAll('[data-hnc-panel]').forEach(el => el.setAttribute('aria-expanded', 'false'));
    this.panel = null;
    this.setBackgroundInert(false);
    if (restoreFocus) this.focusDrawer(this.querySelector(`[data-hnc-panel="${name}"]`));
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html').querySelector(selector) : null;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  replaceSections(sections) {
    const fresh = this.getSectionDOM(sections?.['cart-drawer'], '.drawer__inner');
    if (!fresh) throw new Error('Cart updated, but the drawer could not refresh. Reopen your cart before retrying.');
    const scrollTop = this.querySelector('[data-hnc-scroll]')?.scrollTop || 0;
    const focused = document.activeElement;
    const key = focused?.closest('[data-key]')?.dataset.key;
    const delta = focused?.dataset.hncDelta;
    const focusId = focused?.id;
    const panel = this.panel;
    const draft = this.querySelector('#HncCartNote')?.value;
    const code = this.querySelector('#HncDiscountCode')?.value;
    this.querySelector('.drawer__inner').replaceWith(fresh);
    this.classList.toggle('is-empty', Number(fresh.dataset.count) === 0);
    const bubble = document.getElementById('cart-icon-bubble');
    const newBubble = this.getSectionDOM(sections['cart-icon-bubble']);
    if (bubble && newBubble) bubble.innerHTML = newBubble.innerHTML;
    this.querySelector('[data-hnc-scroll]').scrollTop = scrollTop;
    this.panel = null;
    if (panel && Number(fresh.dataset.count)) {
      if (panel === 'note' && this.querySelector('#HncCartNote')) this.querySelector('#HncCartNote').value = draft || '';
      if (panel === 'coupon') this.querySelector('#HncDiscountCode').value = code || '';
      this.openPanel(panel);
    } else {
      const row = [...this.querySelectorAll('[data-key]')].find(el => el.dataset.key === key);
      const nextFocus = row?.querySelector(`[data-hnc-delta="${delta}"]`) || (focusId ? this.querySelector(`#${CSS.escape(focusId)}`) : null);
      this.focusDrawer(nextFocus);
    }
  }

  async refresh(force = false) {
    if (this.busy && !force) { this.refreshQueued = true; return; }
    const version = ++this.version;
    const response = await fetch(`${this.root}cart?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Unable to refresh cart.');
    const sections = await response.json();
    if (version === this.version) this.replaceSections(sections);
  }

  // Called by existing product-form and quick-add after a successful add.
  async renderContents(state) {
    ++this.version;
    try {
      if (state.sections?.['cart-drawer']) this.replaceSections(state.sections);
      else await this.refresh();
      this.open();
    } catch (error) {
      this.open();
      this.status(error.message);
    }
  }

  status(message) {
    const target = this.panel ? this.querySelector(`[data-hnc-sheet="${this.panel}"] [data-hnc-panel-status]`) : this.querySelector('[data-hnc-status]');
    if (target) target.textContent = message;
  }

  async mutate(endpoint, payload, message) {
    if (this.busy) return;
    this.busy = true;
    ++this.version; // Invalidate older read requests before writing.
    this.setAttribute('aria-busy', 'true');
    this.status('Updating cart…');
    let saved = false;
    try {
      const response = await fetch(this.root + endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname }),
      });
      const state = await response.json();
      if (!response.ok || state.errors) throw new Error(typeof state.description === 'string' ? state.description : typeof state.errors === 'string' ? state.errors : 'The cart could not be updated.');
      saved = true;
      if (state.sections?.['cart-drawer']) this.replaceSections(state.sections);
      else await this.refresh(true); // A successful mutation can still return null section HTML.
      this.status(typeof message === 'function' ? message(state) : message || 'Cart updated.');
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hnc-cart', cartData: state });
      return state;
    } catch (error) {
      this.status(saved ? 'Your change was saved, but the cart could not refresh. Reopen the cart before retrying.' : `${error.message || 'Connection interrupted.'} Check your cart before retrying.`);
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      if (this.refreshQueued) {
        this.refreshQueued = false;
        this.refresh().catch(() => this.status('Please reopen your cart to refresh it.'));
      }
    }
  }

  applyCoupon(code, remove = false) {
    if (!code && !remove) { this.status('Enter a coupon code.'); return; }
    return this.mutate('cart/update.js', { discount: code }, (cart) => {
      if (remove) return 'Coupon codes removed. Automatic offers may still apply.';
      const applications = [...(cart.cart_level_discount_applications || []), ...(cart.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(a => a.discount_application))];
      const applied = (cart.discount_codes || []).some(d => d.applicable && d.code.toLowerCase() === code.toLowerCase()) || applications.some(a => a?.type === 'discount_code' && (a.code || a.title || '').toLowerCase() === code.toLowerCase());
      return applied ? `Coupon applied: ${code}` : 'This code is not applicable to this cart, or a better offer is active.';
    });
  }

  handleClick(event) {
    const button = event.target.closest('button, a');
    if (!button) {
      if (event.target.matches('[data-hnc-close]')) this.close();
      return;
    }
    if (button.matches('[data-hnc-details]')) {
      this.querySelector('#HncCartSummary')?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      this.querySelector('#HncCartSummary')?.focus({ preventScroll: true });
      return;
    }
    if (button.matches('[data-hnc-close]')) { this.close(); return; }
    if (button.matches('[data-hnc-close-panel]')) { this.closePanel(); return; }
    if (button.matches('[data-hnc-panel]')) { this.openPanel(button.dataset.hncPanel); return; }
    if (this.busy && button.closest('.hnc-cart-checkout')) { event.preventDefault(); event.stopImmediatePropagation(); return; }
    if (this.busy) return;
    if (button.matches('[data-hnc-delta]')) {
      const row = button.closest('[data-key]');
      const step = Number(row.dataset.step) || 1;
      const quantity = Number(row.dataset.quantity) + Number(button.dataset.hncDelta) * step;
      const next = quantity < Number(row.dataset.min) ? 0 : quantity;
      if (row.dataset.max && next > Number(row.dataset.max)) { this.status(`You can add up to ${row.dataset.max} of this item.`); return; }
      this.mutate('cart/change.js', { id: row.dataset.key, quantity: next });
    } else if (button.matches('[data-hnc-add]')) {
      this.mutate('cart/add.js', { items: [{ id: Number(button.dataset.hncAdd), quantity: 1 }] }, 'Added to cart.');
    } else if (button.matches('[data-hnc-save-note]')) {
      this.mutate('cart/update.js', { note: this.querySelector('#HncCartNote').value }, 'Order note saved.');
    } else if (button.matches('[data-hnc-offer]')) {
      this.querySelector('#HncDiscountCode').value = button.dataset.hncOffer;
      this.applyCoupon(button.dataset.hncOffer);
    } else if (button.matches('[data-hnc-apply]')) {
      this.applyCoupon(this.querySelector('#HncDiscountCode').value.trim());
    } else if (button.matches('[data-hnc-remove-codes]')) {
      this.querySelector('#HncDiscountCode').value = '';
      this.applyCoupon('', true);
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
